import React, { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { withErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useLocalStorageState } from '@ole-ui/ole-ui-components';
import {
  Button,
  Pagination,
  PaginationVariant,
  SearchInput,
  Skeleton,
  Title,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Thead, Tbody, Tr, Th, Td } from '@patternfly/react-table';
import CopyIcon from '@patternfly/react-icons/dist/esm/icons/copy-icon';
import GenericViewError from 'components/Error/GenericViewError';
import useMobxStores from 'hooks/useMobxStores';
import { handleError } from 'services/ErrorService';
import type { Organization, OrganizationAdmin } from 'types';
import './styles.scss';

type OrgAdmin = OrganizationAdmin & {
  first_name?: string;
  last_name?: string;
};

const PER_PAGE_OPTIONS = [
  { title: '5', value: 5 },
  { title: '10', value: 10 },
  { title: '25', value: 25 },
];

const SEARCH_STORAGE_KEY = 'management-reporting-companies-search';
const SKELETON_ROW_COUNT = 8;

const formatAdminLabel = (admin: OrgAdmin): string => {
  if (admin.preferred_name) {
    return `${admin.username} (${admin.preferred_name})`;
  }

  if (admin.last_name) {
    return `${admin.username} (${admin.first_name} ${admin.last_name})`;
  }

  return admin.username;
};

const getSearchableText = (org: Organization): string => {
  const admins = ((org.admin_set ?? []) as OrgAdmin[])
    .map(formatAdminLabel)
    .join(' ');
  const orders = (org.order_number_set ?? []).join(' ');

  return `${org.name} ${admins} ${orders}`.toLowerCase();
};

type OrganizationListProps = {
  onEdit: (docId: string) => void;
  onCreate: () => void;
  isCreateDisabled?: boolean;
  isFormModalOpen?: boolean;
};

const OrganizationList = ({
  onEdit,
  onCreate,
  isCreateDisabled = false,
  isFormModalOpen = false,
}: OrganizationListProps) => {
  const { t } = useTranslation();
  const { organizationStore } = useMobxStores();
  const [searchTerm, setSearchTerm] = useLocalStorageState(
    SEARCH_STORAGE_KEY,
    '',
  );
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [activeSortIndex, setActiveSortIndex] = useState(0);
  const [activeSortDirection, setActiveSortDirection] = useState<
    'asc' | 'desc'
  >('asc');
  const [copiedOrgId, setCopiedOrgId] = useState<string | null>(null);
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  const isLoading = organizationStore.isOrganizationsTableLoading;
  const isConfirmationModalOpen = organizationStore.modal.opened;
  const totalCount = organizationStore.organizations.length;

  const focusSearchInput = () => {
    const input = searchContainerRef.current?.querySelector('input');
    if (!input || input.disabled) {
      return;
    }

    input.focus();
    input.select();
  };

  useEffect(() => {
    organizationStore.getAllOrganizations();
  }, [organizationStore]);

  // Keep/restore search focus after initial load and when modals close.
  // Disabled inputs (and modal focus traps) otherwise steal autofocus.
  useEffect(() => {
    if (isFormModalOpen || isConfirmationModalOpen || isLoading) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      focusSearchInput();
    });
    return () => cancelAnimationFrame(frame);
  }, [isLoading, isFormModalOpen, isConfirmationModalOpen]);

  useEffect(
    () => () => {
      if (copyResetTimer.current) {
        clearTimeout(copyResetTimer.current);
      }
    },
    [],
  );

  const filteredOrganizations = useMemo(() => {
    const organizations = organizationStore.organizations as Organization[];
    const lowerSearch = searchTerm.trim().toLowerCase();

    let data = lowerSearch
      ? organizations.filter((org) =>
          getSearchableText(org).includes(lowerSearch),
        )
      : [...organizations];

    data = [...data].sort((a, b) => {
      let aVal = '';
      let bVal = '';

      if (activeSortIndex === 0) {
        aVal = a.name ?? '';
        bVal = b.name ?? '';
      } else if (activeSortIndex === 1) {
        aVal = ((a.admin_set ?? []) as OrgAdmin[])
          .map(formatAdminLabel)
          .join(' ');
        bVal = ((b.admin_set ?? []) as OrgAdmin[])
          .map(formatAdminLabel)
          .join(' ');
      } else {
        aVal = (a.order_number_set ?? []).join(' ');
        bVal = (b.order_number_set ?? []).join(' ');
      }

      const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
      return activeSortDirection === 'asc' ? cmp : -cmp;
    });

    return data;
  }, [
    organizationStore.organizations,
    searchTerm,
    activeSortIndex,
    activeSortDirection,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, perPage]);

  const paginatedOrganizations = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredOrganizations.slice(start, start + perPage);
  }, [filteredOrganizations, page, perPage]);

  const filteredCount = filteredOrganizations.length;
  const hasSearchTerm = Boolean(searchTerm.trim());
  const resultCountLabel = hasSearchTerm
    ? t('Showing {{filtered}} of {{total}}', {
        filtered: filteredCount,
        total: totalCount,
      })
    : null;

  const columnLabels = [
    t('Company name'),
    t('Administrators'),
    t('Order numbers'),
  ];
  const actionsLabel = t('Actions');
  const copyText = t('Copy to clipboard');
  const doneCopyText = t('Successfully copied to clipboard!');

  const handleCopyOrderNumbers = (orgId: string, orders: string[]) => {
    if (!orders.length) {
      return;
    }

    navigator.clipboard.writeText(orders.join(', '));
    setCopiedOrgId(orgId);

    if (copyResetTimer.current) {
      clearTimeout(copyResetTimer.current);
    }

    copyResetTimer.current = setTimeout(() => {
      setCopiedOrgId(null);
    }, 2000);
  };

  const getSortParams = (columnIndex: number) => ({
    sortBy: {
      index: activeSortIndex,
      direction: activeSortDirection,
      defaultDirection: 'asc' as const,
    },
    onSort: (
      _event: React.MouseEvent,
      index: number,
      direction: 'asc' | 'desc',
    ) => {
      setActiveSortIndex(index);
      setActiveSortDirection(direction);
    },
    columnIndex,
  });

  const renderTableBody = () => {
    if (isLoading) {
      return Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <Tr key={`skeleton-row-${rowIndex}`}>
          <Td colSpan={4}>
            <Skeleton
              height="24px"
              width="100%"
              screenreaderText={t('Loading')}
            />
          </Td>
        </Tr>
      ));
    }

    if (paginatedOrganizations.length === 0) {
      return (
        <Tr>
          <Td colSpan={4}>{t('No companies found')}</Td>
        </Tr>
      );
    }

    return paginatedOrganizations.map((org) => {
      const admins = (org.admin_set ?? []) as OrgAdmin[];
      const orders = org.order_number_set ?? [];
      const showCopied = copiedOrgId === org.doc_id;

      return (
        <Tr key={org.doc_id}>
          <Td dataLabel={columnLabels[0]}>{org.name}</Td>
          <Td dataLabel={columnLabels[1]}>
            {admins.map((admin) => (
              <div key={`${org.doc_id}-${admin.username}`}>
                <small>{formatAdminLabel(admin)}</small>
              </div>
            ))}
          </Td>
          <Td dataLabel={columnLabels[2]}>
            <div className="organization-list__order-cell">
              {orders.length > 0 && (
                <Tooltip
                  aria="none"
                  aria-live="polite"
                  content={showCopied ? doneCopyText : copyText}
                >
                  <Button
                    variant="plain"
                    className="organization-list__copy-btn"
                    icon={<CopyIcon />}
                    aria-label={t('Copy order numbers to clipboard')}
                    isDisabled={isLoading}
                    onClick={() => handleCopyOrderNumbers(org.doc_id, orders)}
                  />
                </Tooltip>
              )}
              <div className="organization-list__order-values">
                {orders.map((order) => (
                  <div key={`${org.doc_id}-${order}`}>
                    <small>{order}</small>
                  </div>
                ))}
              </div>
            </div>
          </Td>
          <Td dataLabel={actionsLabel} modifier="fitContent">
            <Button
              variant="secondary"
              isDisabled={isLoading}
              onClick={() => {
                onEdit(org.doc_id);
              }}
            >
              {t('Edit')}
            </Button>
          </Td>
        </Tr>
      );
    });
  };

  return (
    <div className="organization-list">
      <Title headingLevel="h3" className="organization-list__title">
        {t('Companies')}
      </Title>
      <div className="organization-list__toolbar">
        <div className="organization-list__toolbar-left">
          <div ref={searchContainerRef} className="organization-list__search">
            <SearchInput
              placeholder={t('Search by company, admin, or order number')}
              value={searchTerm}
              onChange={(_event, value) => setSearchTerm(value)}
              onClear={() => setSearchTerm('')}
              aria-label={t('Search companies')}
            />
          </div>
          {!isLoading && resultCountLabel && (
            <span
              className="organization-list__result-count"
              aria-live="polite"
            >
              {resultCountLabel}
            </span>
          )}
        </div>
        <Button
          variant="primary"
          onClick={onCreate}
          isDisabled={isCreateDisabled || isLoading}
        >
          {t('Create company')}
        </Button>
      </div>
      <Table
        className="organization-list__table"
        aria-label={t('Companies')}
        variant="compact"
        isStriped
        aria-busy={isLoading}
      >
        <Thead>
          <Tr>
            {columnLabels.map((label, index) => (
              <Th
                key={label}
                sort={isLoading ? undefined : getSortParams(index)}
              >
                {label}
              </Th>
            ))}
            <Th modifier="fitContent">{actionsLabel}</Th>
          </Tr>
        </Thead>
        <Tbody>{renderTableBody()}</Tbody>
      </Table>
      <Pagination
        className="organization-list__pagination"
        itemCount={isLoading ? 0 : filteredOrganizations.length}
        perPage={perPage}
        page={page}
        onSetPage={(_event, newPage) => setPage(newPage)}
        onPerPageSelect={(_event, newPerPage) => {
          setPerPage(newPerPage);
          setPage(1);
        }}
        perPageOptions={PER_PAGE_OPTIONS}
        variant={PaginationVariant.bottom}
        widgetId="organization-list-pagination"
        aria-label={t('Companies pagination')}
        isDisabled={isLoading}
      />
    </div>
  );
};

export default withErrorBoundary(
  observer(OrganizationList),
  GenericViewError,
  handleError,
);
